import React, { useState } from 'react';
import { useOffline } from '../context/OfflineContext';
import { Stream } from '../services/streams';
import './DownloadButton.css';

interface DownloadButtonProps {
  stream: Stream;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

const DownloadButton: React.FC<DownloadButtonProps> = ({
  stream,
  size = 'medium',
  showLabel = true
}) => {
  const {
    isStreamDownloaded,
    downloadStream,
    deleteDownload,
    downloadingStreams,
    isOnline
  } = useOffline();

  const [isDeleting, setIsDeleting] = useState(false);
  const isDownloaded = isStreamDownloaded(stream.streamId);
  const downloadProgress = downloadingStreams.get(stream.streamId);
  const isDownloading = downloadProgress?.status === 'downloading';

  const handleDownload = async () => {
    if (!isOnline) {
      alert('You need to be online to download streams');
      return;
    }

    try {
      await downloadStream(stream);
    } catch (error: any) {
      alert(error.message || 'Failed to download stream');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this download?')) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteDownload(stream.streamId);
    } catch (error: any) {
      alert(error.message || 'Failed to delete download');
    } finally {
      setIsDeleting(false);
    }
  };

  const getButtonClass = () => {
    const classes = ['download-button', `size-${size}`];
    if (isDownloaded) classes.push('downloaded');
    if (isDownloading) classes.push('downloading');
    if (isDeleting) classes.push('deleting');
    return classes.join(' ');
  };

  const renderIcon = () => {
    if (isDeleting) {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="icon-spin">
          <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
        </svg>
      );
    }

    if (isDownloading) {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
          <circle
            cx="10"
            cy="10"
            r="8"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray={`${(downloadProgress?.percentage || 0) * 0.5} 50`}
            strokeDashoffset="12.5"
            transform="rotate(-90 10 10)"
            className="progress-circle"
          />
        </svg>
      );
    }

    if (isDownloaded) {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M16 7L8 15L4 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }

    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 3V13M10 13L6 9M10 13L14 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3 13V15C3 16.1046 3.89543 17 5 17H15C16.1046 17 17 16.1046 17 15V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  };

  const getButtonText = () => {
    if (!showLabel) return null;

    if (isDeleting) return 'Deleting...';
    if (isDownloading) return `${Math.round(downloadProgress?.percentage || 0)}%`;
    if (isDownloaded) return 'Downloaded';
    return 'Download';
  };

  const handleClick = () => {
    if (isDownloading || isDeleting) return;

    if (isDownloaded) {
      handleDelete();
    } else {
      handleDownload();
    }
  };

  return (
    <button
      className={getButtonClass()}
      onClick={handleClick}
      disabled={isDownloading || isDeleting || (!isOnline && !isDownloaded)}
      title={isDownloaded ? 'Delete download' : 'Download for offline playback'}
    >
      {renderIcon()}
      {showLabel && <span className="button-text">{getButtonText()}</span>}
    </button>
  );
};

export default DownloadButton;
